import type { AdClip, AudioTrack, PlayerInterface, TextTrack } from '@foundation-player/loader';
import type { Readable } from 'svelte/store';
import type { NextMediumMetadata, PlayerStateContext, SessionState } from '../types/player-state';
import { AdvertisingPlayerStore } from './Advertising';
import { AudioTracksPlayerStore } from './AudioTracks';
import { CurrentTimePlayerStore } from './CurrentTime';
import { DurationPlayerStore } from './Duration';
import { LiveMediumPlayerStore } from './LiveMedium';
import { MetadataPlayerStore } from './Metadata';
import { PlaybackSpeedPlayerStore } from './PlaybackSpeed';
import { PlayingPlayerStore } from './Playing';
import { SessionStatePlayerStore } from './SessionState';
import { StallingPlayerStore } from './Stalling';
import { TextTrackPlayerStore } from './TextTrack';
import { VolumePlayerStore } from './Volume';

// We should not import from the foundation-ui. However, it would require a huge
// amount of work to separate the Foundation-UI parts from the PlayerState. So
// we keep it here.
// TODO: Separate shared and foundation-ui PlayerState.
import type { UIConfig } from '../../foundation-ui/types/ui-config';

export class PlayerStore implements PlayerStateContext {
  // metadata
  readonly poster: Readable<string | undefined>;
  readonly headline: Readable<string | undefined>;
  readonly secondaryHeadline: Readable<string | undefined>;
  readonly nextMediumMetadata: Readable<NextMediumMetadata | undefined>;
  readonly linkToShare: Readable<string | undefined>;

  // stalling
  readonly stalling: Readable<boolean>;

  // volume
  readonly muted: Readable<boolean>;
  readonly volume: Readable<number>;

  // playbackSpeed
  readonly playbackSpeed: Readable<number>;

  // advertising
  readonly adClip: Readable<AdClip | null>;
  readonly adDuration: Readable<number>;
  readonly adPlaying: Readable<boolean>;
  readonly adSlotIsSkippable: Readable<boolean>;
  readonly commercialPlaying: Readable<boolean>;
  readonly currentAdTime: Readable<number>;
  readonly indexOfCurrentCommercialAdClip: Readable<number>;
  readonly interactiveAdPlaying: Readable<boolean>;
  readonly linearAdPlaying: Readable<boolean>;
  readonly numberOfCommercialAdClips: Readable<number>;
  readonly sponsoredAdPlaying: Readable<boolean>;

  // audio tracks
  readonly availableAudioTracks: Readable<AudioTrack[]>;
  readonly activeAudioTrack: Readable<AudioTrack | undefined>;

  // playing
  readonly playing: Readable<boolean>;
  readonly awaitingPlaying: Readable<boolean>;

  // text track
  readonly availableTextTracks: Readable<TextTrack[]>;
  readonly activeTextTrack: Readable<TextTrack | undefined>;

  // live medium
  readonly liveMedium: Readable<boolean>;

  // currentTime
  readonly currentTime: Readable<number>;

  // duration
  readonly durationInSeconds: Readable<number>;

  // session state
  readonly sessionState: Readable<SessionState>;

  readonly #modules: Array<{ initializeStateFromPlayer: (player: PlayerInterface) => void }> = [];

  constructor(uiConfig?: UIConfig) {
    const metadataPlayerStore = new MetadataPlayerStore(uiConfig);
    this.headline = metadataPlayerStore.headline;
    this.poster = metadataPlayerStore.poster;
    this.secondaryHeadline = metadataPlayerStore.secondaryHeadline;
    this.nextMediumMetadata = metadataPlayerStore.nextMediumMetadata;
    this.linkToShare = metadataPlayerStore.linkToShare;
    this.#modules.push(metadataPlayerStore);

    const volumePlayerStore = new VolumePlayerStore();
    this.muted = volumePlayerStore.muted;
    this.volume = volumePlayerStore.volume;
    this.#modules.push(volumePlayerStore);

    const playbackSpeedPlayerStore = new PlaybackSpeedPlayerStore();
    this.playbackSpeed = playbackSpeedPlayerStore.playbackSpeed;
    this.#modules.push(playbackSpeedPlayerStore);

    const stallingPlayerStore = new StallingPlayerStore();
    this.stalling = stallingPlayerStore.stalling;
    this.#modules.push(stallingPlayerStore);

    const currentTimePlayerStore = new CurrentTimePlayerStore();
    this.currentTime = currentTimePlayerStore.currentTime;
    this.#modules.push(currentTimePlayerStore);

    const durationPlayerStore = new DurationPlayerStore(uiConfig);
    this.durationInSeconds = durationPlayerStore.durationInSeconds;
    this.#modules.push(durationPlayerStore);

    // needs to be created after the CurrentTimePlayerStore, because it needs
    // the currentTime
    const advertisingPlayerStore = new AdvertisingPlayerStore(this.currentTime);
    this.adClip = advertisingPlayerStore.adClip;
    this.adDuration = advertisingPlayerStore.adDuration;
    this.adPlaying = advertisingPlayerStore.adPlaying;
    this.adSlotIsSkippable = advertisingPlayerStore.adSlotIsSkippable;
    this.commercialPlaying = advertisingPlayerStore.commercialPlaying;
    this.currentAdTime = advertisingPlayerStore.currentAdTime;
    this.indexOfCurrentCommercialAdClip = advertisingPlayerStore.indexOfCurrentCommercialAdClip;
    this.linearAdPlaying = advertisingPlayerStore.linearAdPlaying;
    this.numberOfCommercialAdClips = advertisingPlayerStore.numberOfCommercialAdClips;
    this.sponsoredAdPlaying = advertisingPlayerStore.sponsoredAdPlaying;
    this.interactiveAdPlaying = advertisingPlayerStore.interactiveAdPlaying;
    this.#modules.push(advertisingPlayerStore);

    const audioTracksPlayerStore = new AudioTracksPlayerStore();
    this.activeAudioTrack = audioTracksPlayerStore.activeAudioTrack;
    this.availableAudioTracks = audioTracksPlayerStore.availableAudioTracks;
    this.#modules.push(audioTracksPlayerStore);

    const playingPlayerStore = new PlayingPlayerStore();
    this.awaitingPlaying = playingPlayerStore.awaitingPlaying;
    this.playing = playingPlayerStore.playing;
    this.#modules.push(playingPlayerStore);

    const textTrackPlayerStore = new TextTrackPlayerStore();
    this.activeTextTrack = textTrackPlayerStore.activeTextTrack;
    this.availableTextTracks = textTrackPlayerStore.availableTextTracks;
    this.#modules.push(textTrackPlayerStore);

    const liveMediumPlayerStore = new LiveMediumPlayerStore();
    this.liveMedium = liveMediumPlayerStore.liveMedium;
    this.#modules.push(liveMediumPlayerStore);

    const sessionStatePlayerStore = new SessionStatePlayerStore(uiConfig?.createPlayerOnDemand ?? false);
    this.sessionState = sessionStatePlayerStore.sessionState;
    this.#modules.push(sessionStatePlayerStore);
  }

  initializeStateFromPlayer(player: PlayerInterface) {
    this.#modules.forEach((module) => module.initializeStateFromPlayer(player));
  }
}
